import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import confetti from 'canvas-confetti';

const QuizGame = ({ category, words, onUpdateProgress, onFinish, onBack }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isCorrect, setIsCorrect] = useState(null);
    const [score, setScore] = useState(0);

    const question = words[currentQuestionIndex];

    const fireConfetti = () => {
        confetti({
            particleCount: 150,
            spread: 90,
            origin: { y: 0.6 },
            colors: ['#0073E4', '#FFD1DC', '#CAF0F8', '#E2F0CB']
        });
    };

    const handleOptionSelect = (optionId) => {
        if (selectedOption !== null) return; // Prevent multiple selections

        setSelectedOption(optionId);

        if (optionId === question.correctAnswer) {
            setIsCorrect(true);
            setScore(prev => prev + 1);
            onUpdateProgress(question.id, true);
            fireConfetti();
        } else {
            setIsCorrect(false);
            onUpdateProgress(question.id, false);
        }

        // Move to next question after delay
        setTimeout(() => {
            if (currentQuestionIndex < words.length - 1) {
                setCurrentQuestionIndex(prev => prev + 1);
                setSelectedOption(null);
                setIsCorrect(null);
            } else {
                onFinish(); // Reached end
            }
        }, 2500); // 2.5 second delay to show result
    };

    if (!question) {
        return (
            <div className="w-full flex-grow flex items-center justify-center pb-12">
                <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
                    <h2 className="text-3xl font-black text-slate-800 mb-4">¡Terminaste el Quiz!</h2>
                    <p className="text-xl text-slate-600 mb-8">
                        Respondiste {score} de {words.length} correctamente.
                    </p>
                    <button
                        onClick={onBack}
                        className="bg-[#0073E4] text-white font-bold text-xl py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all"
                    >
                        Volver al menú
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex-grow flex flex-col items-center pb-12">
            <div className={`w-full max-w-2xl bg-white rounded-[2rem] shadow-xl overflow-hidden shadow-${category.color.replace('bg-', '')}/30`}>

                {/* Header */}
                <div className={`${category.color} p-4 flex items-center justify-between`}>
                    <button
                        onClick={onBack}
                        className={`bg-white/40 p-2 rounded-full hover:bg-white/60 transition-colors ${category.textColor}`}
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <div className="flex-grow text-center">
                        <span className={`text-sm font-bold opacity-80 ${category.textColor}`}>
                            Pregunta {currentQuestionIndex + 1} de {words.length}
                        </span>
                    </div>
                </div>

                {/* Question Area */}
                <div className="p-8 pb-4 text-center">
                    <h3 className="text-3xl md:text-4xl font-black text-slate-800 leading-tight">
                        {question.question}
                    </h3>
                </div>

                {/* Options Layout */}
                <div className="p-8 pt-4 pb-12">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {question.options.map((option, idx) => {
                            const isSelected = selectedOption === option.id;
                            let optionClass = "bg-slate-50 border-slate-200 hover:border-[#0073E4] hover:bg-[#F0FCFF]";

                            if (selectedOption !== null) {
                                if (option.id === question.correctAnswer) {
                                    optionClass = "bg-green-100 border-green-400"; // Highlight correct
                                } else if (isSelected && !isCorrect) {
                                    optionClass = "bg-red-100 border-red-400"; // Highlight wrong choice
                                } else {
                                    optionClass = "bg-slate-50 border-slate-200 opacity-50"; // Dim others
                                }
                            }

                            return (
                                <motion.button
                                    key={option.id}
                                    whileHover={selectedOption === null ? { scale: 1.05 } : {}}
                                    whileTap={selectedOption === null ? { scale: 0.95 } : {}}
                                    onClick={() => handleOptionSelect(option.id)}
                                    disabled={selectedOption !== null}
                                    className={`relative flex flex-col items-center justify-center p-6 rounded-2xl border-4 transition-all duration-300 aspect-square ${optionClass}`}
                                >
                                    {option.imagePath ? (
                                        <img
                                            src={option.imagePath}
                                            alt={option.word}
                                            className="w-32 h-32 object-contain mb-4 drop-shadow-md"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ) : null}

                                    {/* Optional text below image */}
                                    <span className="text-xl font-bold text-slate-700 hidden">
                                        {option.word}
                                    </span>

                                    <AnimatePresence>
                                        {isSelected && isCorrect && (
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                className="absolute -top-4 -right-4 bg-white rounded-full p-1"
                                            >
                                                <CheckCircle size={40} className="text-green-500 bg-white rounded-full" />
                                            </motion.div>
                                        )}
                                        {isSelected && !isCorrect && (
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                className="absolute -top-4 -right-4 bg-white rounded-full p-1"
                                            >
                                                <XCircle size={40} className="text-red-500 bg-white rounded-full" />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                </motion.button>
                            );
                        })}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default QuizGame;
